import { ChefId } from '@/types/chef';

export interface ChefVisualTheme {
  id: ChefId;
  cardGradient: string;
  accentColor: string;
  accentColorLight: string;
  iconBg: string;
  borderHover: string;
  glowColor: string;
  pattern?: string;
  traits: {
    creativity: number;
    tradition: number;
    simplicity: number;
    complexity: number;
    quickness: number;
    elaborateness: number;
  };
  signatureStyles: string[];
  visualMotif: string;
}

export const chefVisualThemes: Record<ChefId, ChefVisualTheme> = {
  TestKitchenScientist: {
    id: 'TestKitchenScientist',
    cardGradient: 'from-emerald-50 to-teal-50 dark:from-emerald-950/50 dark:to-teal-950/50',
    accentColor: 'text-emerald-600 dark:text-emerald-400',
    accentColorLight: 'text-emerald-500',
    iconBg: 'bg-gradient-to-br from-emerald-400 to-teal-500',
    borderHover: 'hover:border-emerald-400 dark:hover:border-emerald-600',
    glowColor: 'shadow-emerald-500/20',
    traits: {
      creativity: 40,
      tradition: 70,
      simplicity: 70,
      complexity: 30,
      quickness: 60,
      elaborateness: 40
    },
    signatureStyles: ['Foolproof', 'Tested', 'Reliable'],
    visualMotif: 'precision'
  },
  TheVitalityChef: {
    id: 'TheVitalityChef',
    cardGradient: 'from-green-50 to-lime-50 dark:from-green-950/50 dark:to-lime-950/50',
    accentColor: 'text-green-600 dark:text-green-400',
    accentColorLight: 'text-green-500',
    iconBg: 'bg-gradient-to-br from-green-400 to-lime-500',
    borderHover: 'hover:border-green-400 dark:hover:border-green-600',
    glowColor: 'shadow-green-500/20',
    traits: {
      creativity: 50,
      tradition: 40,
      simplicity: 60,
      complexity: 40,
      quickness: 50,
      elaborateness: 50
    },
    signatureStyles: ['Healthy', 'Nutritious', 'Balanced'],
    visualMotif: 'vitality'
  },
  TheElementalChef: {
    id: 'TheElementalChef',
    cardGradient: 'from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/50',
    accentColor: 'text-amber-600 dark:text-amber-400',
    accentColorLight: 'text-amber-500',
    iconBg: 'bg-gradient-to-br from-amber-400 to-orange-500',
    borderHover: 'hover:border-amber-400 dark:hover:border-amber-600',
    glowColor: 'shadow-amber-500/20',
    traits: {
      creativity: 50,
      tradition: 60,
      simplicity: 80,
      complexity: 20,
      quickness: 40,
      elaborateness: 60
    },
    signatureStyles: ['Seasonal', 'Pure', 'Technique-focused'],
    visualMotif: 'earth'
  },
  TheCulinaryAlchemist: {
    id: 'TheCulinaryAlchemist',
    cardGradient: 'from-purple-50 to-pink-50 dark:from-purple-950/50 dark:to-pink-950/50',
    accentColor: 'text-purple-600 dark:text-purple-400',
    accentColorLight: 'text-purple-500',
    iconBg: 'bg-gradient-to-br from-purple-400 to-pink-500',
    borderHover: 'hover:border-purple-400 dark:hover:border-purple-600',
    glowColor: 'shadow-purple-500/20',
    traits: {
      creativity: 95,
      tradition: 20,
      simplicity: 30,
      complexity: 70,
      quickness: 40,
      elaborateness: 60
    },
    signatureStyles: ['Transformative', 'Creative', 'Surprising'],
    visualMotif: 'magic'
  },
  TheHeritageChef: {
    id: 'TheHeritageChef',
    cardGradient: 'from-lime-50 to-emerald-50 dark:from-lime-950/50 dark:to-emerald-950/50',
    accentColor: 'text-lime-600 dark:text-lime-400',
    accentColorLight: 'text-lime-500',
    iconBg: 'bg-gradient-to-br from-lime-400 to-emerald-500',
    borderHover: 'hover:border-lime-400 dark:hover:border-lime-600',
    glowColor: 'shadow-lime-500/20',
    traits: {
      creativity: 40,
      tradition: 95,
      simplicity: 50,
      complexity: 50,
      quickness: 30,
      elaborateness: 70
    },
    signatureStyles: ['Authentic', 'Traditional', 'Cultural'],
    visualMotif: 'heritage'
  },
  TheEssentialistChef: {
    id: 'TheEssentialistChef',
    cardGradient: 'from-slate-50 to-gray-50 dark:from-slate-950/50 dark:to-gray-950/50',
    accentColor: 'text-slate-600 dark:text-slate-400',
    accentColorLight: 'text-slate-500',
    iconBg: 'bg-gradient-to-br from-slate-400 to-gray-500',
    borderHover: 'hover:border-slate-400 dark:hover:border-slate-600',
    glowColor: 'shadow-slate-500/20',
    traits: {
      creativity: 30,
      tradition: 50,
      simplicity: 95,
      complexity: 5,
      quickness: 70,
      elaborateness: 30
    },
    signatureStyles: ['Minimal', 'Pure', 'Essential'],
    visualMotif: 'minimal'
  }
};

export const getChefVisualTheme = (chefId: ChefId | string): ChefVisualTheme => {
  return chefVisualThemes[chefId as ChefId] || chefVisualThemes.TheCulinaryAlchemist;
};
